import { NonNullableFormBuilder } from '@angular/forms';
import {
  DateInterval,
  mutParseInterval,
  NormalizedDateInterval,
} from '../../util/date';
import {
  differenceInHours,
  differenceInMinutes,
  eachDayOfInterval,
  Interval,
  isWeekend,
  max,
  min,
  setHours,
  setMinutes,
  toDate,
} from 'date-fns';
import { Camp } from '../camp/camp.model';
import { Course } from '../course/course.model';
import { Workshop } from '../workshop/workshop.model';
import { isCourse } from '../course/course.util';
import type { Staff } from '../staff/staff.model';
import { Absence } from './absence';

export enum ScheduleMode {
  Default = 'default',
  Exact = 'exact',
}

export interface DefaultSchedule {
  mode: ScheduleMode.Default;
  schedule: DateInterval;
}

export interface WeekdayInterval extends Interval {
  weekday: number;
}

export interface ExactSchedule {
  mode: ScheduleMode.Exact;
  schedule: DateInterval[];
  defaultWeeklySchedule?: WeekdayInterval[];
}

export type Schedule = DefaultSchedule | ExactSchedule;

export function getScheduleInterval(
  schedule: Schedule,
): NormalizedDateInterval {
  return schedule.mode === ScheduleMode.Default
    ? {
        start: toDate(schedule.schedule.start),
        end: toDate(schedule.schedule.end),
      }
    : {
        start: min(schedule.schedule.map((i) => i.start)),
        end: max(schedule.schedule.map((i) => i.end)),
      };
}

/**
 * Mutate a schedule object to parse all date strings into date objects.
 * @param schedule
 */
export function mutParseSchedule(schedule: Schedule) {
  if (schedule.mode === ScheduleMode.Default) {
    mutParseInterval(schedule.schedule);
  } else if (schedule.mode === ScheduleMode.Exact) {
    schedule.schedule.forEach(mutParseInterval);
  } else {
    // Exhaustiveness check
    ((a: never) => {})(schedule);
    // Runtime check
    throw new Error(`Unknown schedule mode: ${(schedule as any).mode}`);
  }
}

export const createSchedulePickerFormGroup = (fb: NonNullableFormBuilder) => {
  return fb.group({
    mode: fb.control<Schedule['mode']>(ScheduleMode.Default),
    schedule: fb.control<Schedule['schedule']>({
      start: new Date(),
      end: new Date(),
    }),
  });
};
export const getDefaultSchedule = () => ({
  mode: ScheduleMode.Default,
  schedule: { start: Date.now(), end: Date.now() },
});

export function eachWeekdayOfInterval(interval: Interval) {
  return eachDayOfInterval(interval).filter((day) => !isWeekend(day));
}

export function getScheduledWorkingIntervals(schedule: Schedule) {
  if (schedule.mode === ScheduleMode.Exact)
    // Return a clone of the schedule
    return schedule.schedule.map(({ start, end }) => ({ start, end }));
  return eachWeekdayOfInterval(schedule.schedule).map((day) => ({
    // 7.6-hour workday (plus they get (lunch) breaks, but skipped here for simplicity).
    start: setMinutes(setHours(day, 8), 0),
    end: setMinutes(setHours(day, 15), 36),
  }));
}

export function getTargetSchedule(target: Camp | Course | Workshop) {
  return isCourse(target) ? target.camp.schedule : target.schedule;
}

export function getTargetScheduleInterval(
  target: Camp | Course | Workshop,
): NormalizedDateInterval {
  return getScheduleInterval(getTargetSchedule(target));
}

/**
 * Gets the scheduled hours of a schedule expressed in hours, rounded to 1 decimal.
 * @param schedule
 */
export function getScheduledHours(schedule: Schedule): number {
  const scheduledIntervals = getScheduledWorkingIntervals(schedule);

  return (
    Math.round(
      scheduledIntervals.reduce((minutes, interval) => {
        if (differenceInHours(interval.end, interval.start) >= 24) {
          throw new Error(
            'Scheduled work durations should never exceed 24 hours.',
          );
        }
        return minutes + differenceInMinutes(interval.end, interval.start);
      }, 0) / 6,
    ) / 10
  );
}

export function getStaffAbsentHours(absence: Absence, staff: Staff): number {
  const intervals = absence.staff.absences[staff.id] ?? [];
  let absent = 0;
  for (const { start, end } of intervals) {
    absent += differenceInMinutes(end, start) / 6;
  }
  // Absent total hours rounded to 1 decimal
  return Math.floor(absent) / 10;
}
